<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref, useId } from "vue"
import { useRouter } from "vue-router"
import { useI18n } from "vue-i18n"
import { useConfirm } from "primevue"
import { storeToRefs } from "pinia"
import type { Building, BuildingRelationSummary } from "@types"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { useAuthStore } from "@core/stores/auth.store"
import type { ItemBuildingData } from "@core/components/item/ItemBuilding.vue"

// ================================
// PROPS
// ================================
const props = defineProps<{
    readonly: boolean
    /** Bâtiment en consultation seule. */
    disabled?: boolean
}>()

// ================================
// STORES & SERVICES
// ================================
const router = useRouter()
const buildingStore = useBuildingStore()
const authStore = useAuthStore()
const { building } = storeToRefs(buildingStore)
const { isEmployeWriteOrHigher, isClientHomeys } = storeToRefs(authStore)

const { t } = useI18n({
    messages: {
        fr: {
            /** Aligné sur `homeys-legacy` / `component.building_info_relation.titles.heading1` */
            title: "Relations du bien",
            remove_parent_confirm: {
                header: "Retirer le bâtiment parent",
                message:
                    "Êtes-vous sûr de vouloir retirer le lien avec ce bâtiment parent ? Le bien ne sera plus rattaché à ce groupe.",
                accept: "Retirer le lien",
                reject: "Annuler",
            },
            remove_child_confirm: {
                header: "Retirer un bâtiment lié",
                message:
                    "Êtes-vous sûr de vouloir retirer le lien avec ce bâtiment ? Il ne sera plus rattaché à ce bien.",
                accept: "Retirer le lien",
                reject: "Annuler",
            },
        },
    },
})

const confirm = useConfirm()
const componentId = useId()

const dialogParentVisible = ref(false)
const dialogChildVisible = ref(false)
const loadingParentRemove = ref(false)
const loadingChildRemove = ref<Record<string, boolean>>({})

/**
 * Extrait le parent peuplé depuis la réponse API (`_id_parent` objet ou champ dédié).
 */
function getParent(b: Building | null): BuildingRelationSummary | null {
    if (!b) return null
    const raw = (b as unknown as { _id_parent?: unknown })._id_parent
    if (raw && typeof raw === "object" && raw !== null && "_id" in raw) {
        return raw as BuildingRelationSummary
    }
    return null
}

function getChildrenList(b: Building | null): BuildingRelationSummary[] {
    if (!b?.children) return []
    return Object.values(b.children)
}

/**
 * Données affichables par {@link ItemBuilding} à partir d’un résumé parent / enfant.
 */
function getRelationItemBuildingData(
    row: BuildingRelationSummary
): ItemBuildingData {
    return {
        _id: row._id,
        name: row.name,
        address: row.address?.inline_name
            ? { inline_name: row.address.inline_name }
            : undefined,
    }
}

const getNiveau = computed(() => building.value?.infos?.niveau ?? "")

/**
 * Libellés de section et actions alignés sur le legacy
 * (`BuildingInfoRelationParent`, `BuildingInfoRelationListChild`, `components-lang.js`).
 */
const getParentSectionTitle = computed(() => {
    const level = getNiveau.value
    if (["immeuble", "maison", "maison_immeuble"].includes(level)) {
        return "Groupe de bâtiment(s)"
    }
    return "Bâtiment"
})

const getParentEmptyText = computed(() => {
    const level = getNiveau.value
    if (["immeuble", "maison", "maison_immeuble"].includes(level)) {
        return "Aucun groupe de bâtiment associé. Vous pouvez en créer un depuis la page d'accueil."
    }
    return "Aucun bâtiment associé."
})

const getChildSectionTitle = computed(() =>
    getNiveau.value === "groupe" ? "Bâtiment(s)" : "Partie(s) du bâtiment"
)

const getChildEmptyText = computed(() =>
    getNiveau.value === "groupe"
        ? "Aucun bâtiment associé"
        : "Aucune partie du bâtiment associée"
)

const getCreateParentLabel = computed(() => "Créer un bâtiment")

const getSearchParentLabel = computed(() => "Rechercher un bâtiment")

const getCreateChildLabel = computed(() =>
    getNiveau.value === "groupe"
        ? "Créer un bâtiment"
        : "Créer une partie du bâtiment"
)

const getSearchChildLabel = computed(() =>
    getNiveau.value === "groupe"
        ? "Rechercher un bâtiment"
        : "Rechercher une partie du bâtiment"
)

const getBuildingId = computed(
    () => building.value?._id_building ?? building.value?._id ?? ""
)
const getMongoId = computed(
    () => building.value?._id ?? building.value?._id_building ?? ""
)

const getParentRow = computed(() => getParent(building.value))
const getChildrenRows = computed(() => getChildrenList(building.value))

const getShowParentSection = computed(
    () => getNiveau.value && getNiveau.value !== "groupe"
)
const getShowChildrenSection = computed(
    () =>
        !["appartement", "maison_appartement", "maison"].includes(
            getNiveau.value
        )
)

const getIsAppartementLevel = computed(
    () => !["immeuble", "maison_immeuble", "groupe"].includes(getNiveau.value)
)

const getCanEditRelations = computed(
    () =>
        !props.readonly &&
        !props.disabled &&
        isEmployeWriteOrHigher.value &&
        isClientHomeys.value
)

function getFolderId(): string {
    return building.value?.folder?._id_folder ?? ""
}

/**
 * Affiche la confirmation puis retire le lien avec le bâtiment parent.
 */
function onRequestRemoveParent(parentId: string): void {
    confirm.require({
        group: componentId,
        header: t("remove_parent_confirm.header"),
        message: t("remove_parent_confirm.message"),
        icon: "pi pi-info-circle",
        rejectLabel: t("remove_parent_confirm.reject"),
        rejectProps: {
            label: t("remove_parent_confirm.reject"),
            outlined: true,
            severity: "secondary",
        },
        acceptProps: {
            label: t("remove_parent_confirm.accept"),
            icon: "pi pi-trash",
            severity: "danger",
        },
        accept: () => {
            void onRemoveParent(parentId)
        },
    })
}

async function onRemoveParent(parentId: string): Promise<void> {
    const childId = getMongoId.value
    if (!childId) return
    try {
        loadingParentRemove.value = true
        await buildingStore.removeBuildingChildren(parentId, [childId])
    } finally {
        loadingParentRemove.value = false
    }
}

/**
 * Affiche la confirmation puis retire le lien avec un bâtiment enfant.
 */
function onRequestRemoveChild(childId: string): void {
    confirm.require({
        group: componentId,
        header: t("remove_child_confirm.header"),
        message: t("remove_child_confirm.message"),
        icon: "pi pi-info-circle",
        rejectLabel: t("remove_child_confirm.reject"),
        rejectProps: {
            label: t("remove_child_confirm.reject"),
            outlined: true,
            severity: "secondary",
        },
        acceptProps: {
            label: t("remove_child_confirm.accept"),
            icon: "pi pi-trash",
            severity: "danger",
        },
        accept: () => {
            void onRemoveChild(childId)
        },
    })
}

async function onRemoveChild(childId: string): Promise<void> {
    const parentId = getMongoId.value
    if (!parentId) return
    try {
        loadingChildRemove.value = {
            ...loadingChildRemove.value,
            [childId]: true,
        }
        await buildingStore.removeBuildingChildren(parentId, [childId])
    } finally {
        const next = { ...loadingChildRemove.value }
        delete next[childId]
        loadingChildRemove.value = next
    }
}

function onCreateParent(): void {
    const b = building.value
    if (!b) return
    void router.push({
        name: "PageBuildingAppend",
        query: {
            buildingChild: getMongoId.value,
            buildingChildLevel: getNiveau.value,
            folderId: getFolderId(),
        },
    })
}

function onCreateChild(): void {
    void router.push({
        name: "PageBuildingAppend",
        query: {
            buildingParent: getMongoId.value,
            buildingParentLevel: getNiveau.value,
            folderId: getFolderId(),
        },
    })
}

function onOpenRelationBuilding(buildingId: string): void {
    void router.push({ path: `/building/${buildingId}` })
}
</script>

<template>
    <BaseTitledCard
        class="w-full shadow-sm"
        :pt="{ content: 'flex flex-col gap-6' }"
    >
        <template #header>
            {{ t("title") }}
        </template>
        <template #content>
            <section v-if="getShowParentSection" class="space-y-3">
                <h3 class="text-sm font-medium text-surface-700">
                    {{ getParentSectionTitle }} :
                </h3>
                <div
                    v-if="getParentRow"
                    class="flex flex-wrap items-center justify-between gap-3 rounded border border-surface-200 bg-surface-0 px-4 py-3"
                >
                    <ItemBuilding
                        class="min-w-0 flex-1"
                        :building="getRelationItemBuildingData(getParentRow)"
                        :show-tags="false"
                        @click="onOpenRelationBuilding"
                    />
                    <BaseButton
                        v-if="getCanEditRelations"
                        type="button"
                        icon="pi pi-trash"
                        severity="danger"
                        rounded
                        text
                        :loading="loadingParentRemove"
                        :aria-label="'Retirer le lien'"
                        @click="onRequestRemoveParent(getParentRow._id)"
                    />
                </div>
                <p
                    v-else
                    class="text-center text-sm italic text-surface-600 py-2"
                >
                    {{ getParentEmptyText }}
                </p>
                <div
                    v-if="
                        getCanEditRelations &&
                        !getParentRow &&
                        getIsAppartementLevel
                    "
                    class="flex flex-wrap justify-center gap-3"
                >
                    <BaseButton
                        type="button"
                        severity="secondary"
                        :label="getCreateParentLabel"
                        @click="onCreateParent"
                    />
                    <BaseButton
                        type="button"
                        severity="secondary"
                        :label="getSearchParentLabel"
                        @click="dialogParentVisible = true"
                    />
                </div>
            </section>

            <section v-if="getShowChildrenSection" class="space-y-3">
                <h3 class="text-sm font-medium text-surface-700">
                    {{ getChildSectionTitle }} :
                </h3>
                <div
                    v-for="ch in getChildrenRows"
                    :key="ch._id"
                    class="flex flex-wrap items-center justify-between gap-3 rounded border border-surface-200 bg-surface-0 px-4 py-3"
                >
                    <ItemBuilding
                        class="min-w-0 flex-1"
                        :building="getRelationItemBuildingData(ch)"
                        :show-tags="false"
                        @click="onOpenRelationBuilding"
                    />
                    <BaseButton
                        v-if="getCanEditRelations"
                        type="button"
                        icon="pi pi-trash"
                        severity="danger"
                        rounded
                        text
                        :loading="!!loadingChildRemove[ch._id]"
                        :aria-label="'Retirer le lien'"
                        @click="onRequestRemoveChild(ch._id)"
                    />
                </div>
                <p
                    v-if="getChildrenRows.length === 0"
                    class="text-center text-sm italic text-surface-600 py-2"
                >
                    {{ getChildEmptyText }}
                </p>
                <div
                    v-if="getCanEditRelations"
                    class="flex flex-wrap justify-center gap-3"
                >
                    <BaseButton
                        type="button"
                        severity="secondary"
                        :label="getCreateChildLabel"
                        @click="onCreateChild"
                    />
                    <BaseButton
                        type="button"
                        severity="secondary"
                        :label="getSearchChildLabel"
                        @click="dialogChildVisible = true"
                    />
                </div>
            </section>

            <BuildingRelationParentDialog
                v-model:visible="dialogParentVisible"
                :building-id="getBuildingId"
                :niveau="getNiveau"
            />
            <BuildingRelationChildDialog
                v-model:visible="dialogChildVisible"
                :building-id="getBuildingId"
                :niveau="getNiveau"
            />
            <BaseConfirmDialog :group="componentId" />
        </template>
    </BaseTitledCard>
</template>
